import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardBody, Checkbox, Radio, RadioGroup } from '@structural-climbing/ui'
import { CompetitionAbout } from './CompetitionAbout'
import { CategoryPicker } from './CategoryPicker'
import { ProfileNeededCallout } from './ProfileNeededCallout'
import { PrivateProfileCallout } from './PrivateProfileCallout'
import { formatWallClockTime } from './wallClock'
import type { CompetitionDetailView } from './types'

interface Props {
  competition: CompetitionDetailView
  joining: boolean
  error: string | null
  onJoin: (categoryId: string, sessionId: string | null) => void
  onProfileMadePublic: () => Promise<void>
}

/** Everything a member sees before they are in: what it is, what they can win. */
/** Minor units to something a person reads: 1300 RON becomes "13,00 RON". */
function formatFee(cents: number, currency: string | null, locale: string): string {
  const amount = cents / 100
  if (!currency) return String(amount)
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
  } catch {
    // An unknown code should still show the number rather than nothing.
    return `${amount} ${currency}`
  }
}

export function CompetitionIntro({
  competition,
  joining,
  error,
  onJoin,
  onProfileMadePublic,
}: Props) {
  const { t, i18n } = useTranslation()
  const [sessionId, setSessionId] = useState<string | null>(competition.sessions[0]?.id ?? null)
  // Preselect the first category they can actually enter, so the common case of
  // one eligible category is a single tap.
  const [categoryId, setCategoryId] = useState<string | null>(
    competition.categories.find((c) => c.eligible)?.id ?? null,
  )

  // Step two of Etto's flow: pick a category, then accept the rules. Required
  // because entering puts your name on a page anyone can open.
  const [accepted, setAccepted] = useState(false)

  const ended = competition.state === 'ended'
  const noneEligible = !competition.categories.some((c) => c.eligible)
  // Nothing is wrong with the member, only with what their profile says. That
  // gets its own callout rather than "no category is open to you".
  const isPrivate = competition.join_blocked_reason === 'private'
  const missingField =
    competition.join_blocked_reason === 'gender'
      ? 'gender'
      : competition.join_blocked_reason === 'dob'
        ? 'dob'
        : null

  return (
    <div className="flex flex-col gap-3">
      <CompetitionAbout description={competition.description} prizes={competition.prizes} />

      {/* What it costs and who it is for, before anything is chosen. */}
      <Card className="shadow-sm">
        <CardBody className="gap-1 py-3">
          <p className="text-sm font-medium">
            {competition.join_policy === 'open'
              ? t('competitions.openToAll')
              : t('competitions.membersOnly')}
          </p>
          {competition.entry_fee_cents !== null && (
            <p className="text-sm text-foreground/70">
              {t('competitions.entryFee', {
                amount: formatFee(
                  competition.entry_fee_cents,
                  competition.entry_fee_currency,
                  i18n.language,
                ),
              })}
              <span className="block text-xs text-foreground/50">
                {t('competitions.entryFeeAtDesk')}
              </span>
            </p>
          )}
        </CardBody>
      </Card>

      {isPrivate && !ended && <PrivateProfileCallout onMadePublic={onProfileMadePublic} />}
      {missingField && !ended && <ProfileNeededCallout field={missingField} />}

      {competition.categories.length > 0 && (
        <Card className="shadow-sm">
          <CardBody className="gap-3">
            <h2 className="font-bold">{t('competitions.chooseCategory')}</h2>
            <CategoryPicker
              categories={competition.categories}
              value={categoryId}
              onChange={setCategoryId}
            />
            {noneEligible && !missingField && (
              <p className="text-sm text-warning">{t('competitions.noEligibleCategory')}</p>
            )}
          </CardBody>
        </Card>
      )}

      {competition.sessions.length > 0 && (
        <Card className="shadow-sm">
          <CardBody className="gap-3">
            <h2 className="font-bold">{t('competitions.chooseSession')}</h2>
            <RadioGroup
              value={sessionId ?? ''}
              onValueChange={setSessionId}
              aria-label={t('competitions.chooseSession')}
            >
              {competition.sessions.map((session) => {
                const full = session.seats_left === 0
                return (
                  <Radio key={session.id} value={session.id} isDisabled={full}>
                    <span className="text-sm">
                      {session.name} · {formatWallClockTime(session.starts_at)}–
                      {formatWallClockTime(session.ends_at)}
                      <span className="ml-2 text-xs text-foreground/50">
                        {session.seats_left === null
                          ? t('competitions.groupUncapped')
                          : full
                            ? t('competitions.sessionFull')
                            : t('competitions.sessionSeatsLeft', { count: session.seats_left })}
                      </span>
                    </span>
                  </Radio>
                )
              })}
            </RadioGroup>
          </CardBody>
        </Card>
      )}

      <Card className="shadow-sm">
        <CardBody className="gap-2">
          <h2 className="font-bold">{t('competitions.rulesTitle')}</h2>
          <ul className="list-disc pl-5 text-sm text-foreground/70">
            <li>{t('competitions.rulesFairPlay')}</li>
            {/* Said plainly, because the scoreboard is readable without an
                account and a competitor should know before they enter. */}
            <li>{t('competitions.rulesPublic')}</li>
          </ul>
          <Checkbox isSelected={accepted} onValueChange={setAccepted} size="sm">
            <span className="text-sm">{t('competitions.rulesAccept')}</span>
          </Checkbox>
        </CardBody>
      </Card>

      {error && <p className="text-sm text-danger">{error}</p>}

      <Button
        color="primary"
        size="lg"
        isLoading={joining}
        isDisabled={ended || isPrivate || !categoryId || !accepted}
        onPress={() => categoryId && onJoin(categoryId, sessionId)}
      >
        {ended ? t('competitions.state_ended') : t('competitions.join')}
      </Button>
    </div>
  )
}
